// Theme color resolution by block context, mirroring PostsCarousel.vue.
//
// Theme colors are no longer stored per block (the old `themeColor` hex field).
// Instead every block derives its palette from its `site_context`, so all blocks
// inside one clinic stay visually consistent and a single source of truth governs
// the brand colors. The maps below match PostsCarousel.vue and the Tailwind
// `@theme` tokens in styles/application.css.

export const COLOR_CLASS_BY_SITE = {
  code_medical_center: 'code-medical-center',
  infertility_treatment_clinic: 'infertility-clinic',
  family_clinic: 'family-clinic',
  institute: 'institute',
};

// REVERT NOTE (2026 palette swap): each `pre-2026:` comment is the value used
// before the swap. To roll back, restore those values here AND in
// styles/application.css (@theme) + siteContextBadge.js. See KOLORY-STAN-OBECNY.md.
export const COLOR_RGB_BY_SITE = {
  code_medical_center: '46, 168, 207',        // pre-2026: 12, 74, 110
  infertility_treatment_clinic: '129, 179, 205', // pre-2026: 131, 24, 67
  family_clinic: '40, 182, 155',              // pre-2026: 19, 78, 74
  institute: '46, 168, 207',                  // pre-2026: 76, 29, 149
};

export const COLOR_HEX_BY_SITE = {
  code_medical_center: '#2EA8CF',             // pre-2026: #0c4a6e
  infertility_treatment_clinic: '#81B3CD',    // pre-2026: #831843
  family_clinic: '#28B69B',                   // pre-2026: #134e4a
  institute: '#2EA8CF',                       // pre-2026: #4c1d95
};

// Soft card background tint per context — the light "jasny" brand tint
// (e.g. Search CTA / Contact Info cards, testimonial panels).
export const COLOR_CARD_BY_SITE = {
  code_medical_center: '#D5EEF6',             // pre-2026: #F0F9FF
  infertility_treatment_clinic: '#DAE9F1',    // pre-2026: #fdf2f8
  family_clinic: '#D5F6F0',                   // pre-2026: #F0FDFA
  institute: '#D5EEF6',                       // pre-2026: #F5F3FF
};

// Card border tint per context (slightly stronger than the light card background).
export const COLOR_BORDER_BY_SITE = {
  code_medical_center: '#B3E0EE',             // pre-2026: #E0F2FE
  infertility_treatment_clinic: '#BAD5E4',    // pre-2026: #fce7f3
  family_clinic: '#ABE8DD',                   // pre-2026: #CCFBF1
  institute: '#B3E0EE',                       // pre-2026: #EDE9FE
};

// RGB of the light "jasny" card tint — used for gradient overlays (e.g. the
// hero carousel fades the light brand tint over the slide image).
export const COLOR_CARD_RGB_BY_SITE = {
  code_medical_center: '213, 238, 246',
  infertility_treatment_clinic: '218, 233, 241',
  family_clinic: '213, 246, 240',
  institute: '213, 238, 246',
};

// Shared primary-CTA brand gradient (CODE blue → Family green), used on
// solid buttons like "Szukaj" / "Umów wizytę" across every context.
export const BRAND_GRADIENT = 'linear-gradient(160.691deg, rgb(46, 168, 207) 0%, rgb(40, 182, 155) 100%)';

// Section headings/overlines — neutral dark (Figma Secondary/Gray/800), not brand tint.
export const HEADING_TEXT_COLOR = '#262626';

export const SECONDARY_CLASS_BY_SITE = {
  code_medical_center: 'code-medical-center-secondary',
  infertility_treatment_clinic: 'infertility-clinic-secondary',
  family_clinic: 'family-clinic-secondary',
  institute: 'institute-secondary',
};

const DEFAULT_SITE = 'code_medical_center';

export function resolveSiteContext(siteContext) {
  return siteContext || DEFAULT_SITE;
}

export function themeColorClassFor(siteContext) {
  return COLOR_CLASS_BY_SITE[resolveSiteContext(siteContext)] || COLOR_CLASS_BY_SITE[DEFAULT_SITE];
}

export function themeColorRgbFor(siteContext) {
  return COLOR_RGB_BY_SITE[resolveSiteContext(siteContext)] || COLOR_RGB_BY_SITE[DEFAULT_SITE];
}

export function themeColorHexFor(siteContext) {
  return COLOR_HEX_BY_SITE[resolveSiteContext(siteContext)] || COLOR_HEX_BY_SITE[DEFAULT_SITE];
}

// Search CTA homepage border: CODE blue → Family green, left-to-right so both
// hues read on the wide card (the 160° CTA fill looks almost solid on a frame).
export const SEARCH_CTA_HOME_BORDER_GRADIENT = 'linear-gradient(90deg, #2EA8CF 0%, #28B69B 100%)';

// Search CTA card: 3px brand border. Homepage uses the CODE↔Family gradient;
// clinic contexts use a solid brand hex (double-layer background so both work).
export function themeSearchCtaFrameStyleFor(siteContext) {
  const site = resolveSiteContext(siteContext);
  const hex = themeColorHexFor(site);
  const borderFill = site === DEFAULT_SITE
    ? SEARCH_CTA_HOME_BORDER_GRADIENT
    : `linear-gradient(${hex}, ${hex})`;

  return {
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    backgroundImage: `linear-gradient(#ffffff, #ffffff), ${borderFill}`,
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
  };
}

export function themeCardColorFor(siteContext) {
  return COLOR_CARD_BY_SITE[resolveSiteContext(siteContext)] || COLOR_CARD_BY_SITE[DEFAULT_SITE];
}

export function themeCardColorRgbFor(siteContext) {
  return COLOR_CARD_RGB_BY_SITE[resolveSiteContext(siteContext)] || COLOR_CARD_RGB_BY_SITE[DEFAULT_SITE];
}

export function themeBorderColorFor(siteContext) {
  return COLOR_BORDER_BY_SITE[resolveSiteContext(siteContext)] || COLOR_BORDER_BY_SITE[DEFAULT_SITE];
}

export function themeHeadingColorFor() {
  return HEADING_TEXT_COLOR;
}

export function themeSecondaryClassFor(siteContext) {
  return SECONDARY_CLASS_BY_SITE[resolveSiteContext(siteContext)] || SECONDARY_CLASS_BY_SITE[DEFAULT_SITE];
}
